import React, { createContext, useContext, useEffect, useRef } from 'react';

type TScrollBottomCallback = () => void;

interface IScrollBottomContext {
  /**
   * Register a stable callback ref. Returns an unregister function.
   */
  register: (
    cbRef: React.MutableRefObject<TScrollBottomCallback | undefined>,
  ) => () => void;
}

export const ScrollBottomContext = createContext<IScrollBottomContext>({
  register: () => () => {},
});

/**
 * Hook: register a scrollBottom callback from any child component.
 *
 * - Registers only **once** on mount (via useEffect), unregisters on unmount.
 * - Uses a ref so the Set always invokes the **latest** callback without re-registering.
 * - No work done on re-renders beyond updating the ref.
 * - Re-registers automatically if the Provider re-mounts (new ctx.register identity).
 *
 * @example
 * ```tsx
 * useScrollBottomRegister(nextPage);
 * ```
 */
export function useScrollBottomRegister(cb: TScrollBottomCallback) {
  const ctx = useContext(ScrollBottomContext);

  // Always point to the latest callback — no re-register needed
  const cbRef = useRef(cb);
  cbRef.current = cb;

  // Register on mount (and re-register if Provider re-mounts), unregister on unmount
  useEffect(() => {
    const unregister = ctx.register(cbRef);
    return unregister;
  }, [ctx.register]);
}

/**
 * Hook: get the context directly for more advanced usage.
 */
export function useScrollBottomContext() {
  return useContext(ScrollBottomContext);
}

export type { TScrollBottomCallback, IScrollBottomContext };
